import HttpClient from './HttpService';
import LocalStorage from './LocalStorageService';
import router from '@/router';
import { v4 as uuidv4 } from 'uuid';

const DEVICE_ID_KEY = 'sirimmatrix_device_id';

function getOrCreateDeviceId() {
    let deviceId = localStorage.getItem(DEVICE_ID_KEY);
    if (!deviceId) {
        deviceId = uuidv4();
        localStorage.setItem(DEVICE_ID_KEY, deviceId);
    }
    return deviceId;
}

function getDeviceName() {
    const ua = navigator.userAgent;
    if (/iPhone|iPad|iPod/.test(ua)) return 'iPhone/iPad';
    if (/Android/.test(ua)) return 'Android';
    if (/Mac/.test(ua)) return 'Mac';
    if (/Windows/.test(ua)) return 'Windows';
    if (/Linux/.test(ua)) return 'Linux';
    return 'Navegador';
}

function readStoredAuth() {
    const storedAuth = LocalStorage.get('authenticated_user');
    if (!storedAuth) return false;
    try {
        return JSON.parse(storedAuth);
    } catch (error) {
        LocalStorage.clear();
        return false;
    }
}

function authenticate(username, password) {
    return HttpClient.post('/auth/authenticate', {
        username,
        password,
        device_id:   getOrCreateDeviceId(),
        device_name: getDeviceName(),
    });
}

function loginWithGoogle(id_token) {
    return HttpClient.post('/auth/google', {
        id_token,
        device_id:   getOrCreateDeviceId(),
        device_name: getDeviceName(),
    });
}

function refreshSession() {
    const auth = readStoredAuth();
    if (!auth || !auth.refresh_token) return Promise.reject('no_refresh_token');

    return HttpClient.post('/auth/refresh', {
        refresh_token: auth.refresh_token,
        device_id:     getOrCreateDeviceId(),
    }).then(response => {
        const data = response.data && response.data.data ? response.data.data : response.data;
        const newToken = data.token;
        const user = data.user || auth;
        storeLocalData({ token: newToken, refresh_token: auth.refresh_token, user });
        return newToken;
    });
}

function createAccount({ name, email, password }) {
    return HttpClient.post('/auth/createaccount', { name, email, password });
}

function confirmEmail(token) {
    return HttpClient.post(`/auth/confirm-email/${token}`, {
        device_id:   getOrCreateDeviceId(),
        device_name: getDeviceName(),
    });
}

function resendConfirmation(email) {
    return HttpClient.post('/auth/resend-confirmation', { email });
}

function forgotPassword(email) {
    return HttpClient.post('/auth/forgot-password', { email });
}

function validateResetPasswordToken(token) {
    return HttpClient.get(`/auth/reset-password/${token}`);
}

function resetPasswordWithToken(token, password) {
    return HttpClient.post(`/auth/reset-password/${token}`, { password });
}

function storeLocalData({ token, refresh_token, user }) {
    const deviceId = localStorage.getItem(DEVICE_ID_KEY);
    LocalStorage.clear();
    if (deviceId) localStorage.setItem(DEVICE_ID_KEY, deviceId);

    const loggedUser = {
        token,
        refresh_token,
        id:                   user.id,
        uuid:                 user.uuid || null,
        company:              user.company,
        name:                 user.name,
        email:                user.email || null,
        photo_url:            user.photo_url || null,
        role:                 user.role || 'member',
        auth_provider:        user.auth_provider || 'password',
        subscription_status:  user.subscription_status || null,
        days_left:            user.days_left !== undefined ? user.days_left : null,
        plan:                 user.plan || 'basico',
        google_avatar_url:    user.google_avatar_url || null,
    };

    LocalStorage.set('authenticated_user', loggedUser);
}

function getAuthInfo() {
    return readStoredAuth();
}

function isAuthenticated() {
    const authInfo = readStoredAuth();
    if (!authInfo) return false;
    const { token, id } = authInfo;
    return !!(token && id);
}

function isOwnerOrAdmin() {
    const authInfo = readStoredAuth();
    return !!authInfo && (authInfo.role === 'owner' || authInfo.role === 'admin');
}

async function logout() {
    const deviceId = getOrCreateDeviceId();

    try {
        await HttpClient.delete(`/auth/sessions/${deviceId}`);
    } catch (e) { /* segue com o logout local mesmo se a chamada falhar */ }

    LocalStorage.clear();
    // O device_id identifica esse navegador entre sessões — sem preservar ele aqui,
    // cada logout+login gera um "dispositivo" novo e nunca invalidado.
    localStorage.setItem(DEVICE_ID_KEY, deviceId);

    router.push({ name: 'login' });
}

export default {
    logout,
    authenticate,
    loginWithGoogle,
    refreshSession,
    storeLocalData,
    getAuthInfo,
    isAuthenticated,
    isOwnerOrAdmin,
    createAccount,
    confirmEmail,
    resendConfirmation,
    getOrCreateDeviceId,
    forgotPassword,
    validateResetPasswordToken,
    resetPasswordWithToken,
};
